/**
 * Flexible booking, inline: there is no time to pick, so the panel states that
 * plainly and offers the one thing still open — whether the client names the
 * creative (`poc:#booked-summary` in its flexible state).
 *
 * Saying "to define with the Creative" where the date would have been is the
 * point: the field is not missing, it is answered, and the client should not go
 * looking for a calendar that is deliberately absent.
 *
 * ⚠️ DESIGN PASS. Choosing this is what makes the third-party organization the
 * order's meeting mode; that mapping is not wired yet.
 */

import React from 'react'
import { Stack, Typography } from '@mui/material'
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined'
import { useTranslation } from 'react-i18next'

import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import type { RevampCreative } from 'dataQueries/purchaseFlowRevampCreatives.query'

import { ChooseCreativeToggle } from './ChooseCreativeToggle.component'
import { ScheduleCreativeList } from './ScheduleCreativeList.component'

interface Props {
  canChooseCreative: boolean
  choosingCreative: boolean
  onToggleChoosingCreative: (next: boolean) => void
  creatives: ReadonlyArray<RevampCreative>
  creativeId?: string
  onPickCreative: (id: string) => void
  isCreativesLoading?: boolean
  /** The last list came back empty, so the toggle switched itself off. */
  noCreativesAvailable?: boolean
}

export const FlexibleBookingPanel: React.FC<Props> = ({
  canChooseCreative,
  choosingCreative,
  onToggleChoosingCreative,
  creatives,
  creativeId,
  onPickCreative,
  isCreativesLoading = false,
  noCreativesAvailable = false,
}) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  return (
    <Stack gap={1.6} data-cy="pf-revamp-flexible-booking-panel">

      {/* Same creative switch as fixed booking; its list opens right under it, the date follows. */}
      {canChooseCreative &&
      <Stack gap={0.8}>

        <ChooseCreativeToggle
          checked={choosingCreative}
          onToggle={onToggleChoosingCreative}
          noFavourites={noCreativesAvailable}
        />

        {/* While the list loads the spinner sits under the switch: opening the list column first and closing it
            again when no creative comes back made the panel jump. */}
        {choosingCreative && isCreativesLoading &&
          <ScheduleCreativeList creatives={[]} onSelect={onPickCreative} isLoading />
        }

      </Stack>
      }

      {canChooseCreative && choosingCreative && !isCreativesLoading && creatives.length > 0 &&
        <ScheduleCreativeList
          creatives={creatives}
          value={creativeId}
          onSelect={onPickCreative}
        />
      }

      <Stack gap={0.4}>

        <Stack direction="row" alignItems="center" gap={0.4}>
          <CalendarMonthOutlined sx={{ fontSize: '1.6rem', color: GRAY_700 }} />
          <Typography variant="text-sm" color={GRAY_700}>
            {t('purchase_flow_revamp:instructions.schedule.date_time_label')}
          </Typography>
        </Stack>

        <Typography variant="text-md" fontWeight={550} color={GRAY_900}>
          {t('purchase_flow_revamp:instructions.schedule.to_define_with_creative')}
        </Typography>

      </Stack>

    </Stack>
  )
}
